import React, { createContext, useState, useCallback, ReactNode } from "react";
import api from "../../api/api";
import { ApiResponse, PaginationInfo } from "../../types/api";
import { WageSlip } from "../../types/models";
import { WageSlipContextType } from "../../types/context";

export const WageSlipContext = createContext<WageSlipContextType | undefined>(undefined);

interface WageSlipProviderProps {
  children: ReactNode;
}

export const WageSlipProvider: React.FC<WageSlipProviderProps> = ({ children }) => {
  const [wageSlips, setWageSlips] = useState<WageSlip[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWageSlips = useCallback(async (
    projectId: number | null = null,
    page: number = 1,
    perPage: number = 15
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      let url = '/wage-slips';
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (projectId) {
        url = `/projects/${projectId}/wage-slips`;
      }
      if (params.toString()) url += projectId ? `?${params.toString()}` : `?${params.toString()}`;
      
      const response = await api.get<ApiResponse<any>>(url);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let wageSlipsData: any[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          wageSlipsData = responseData.data.data;
          paginationData = responseData.data.pagination || (responseData as any).pagination || responseData.meta?.pagination || null;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          wageSlipsData = responseData.data;
          paginationData = (responseData as any).pagination || responseData.meta?.pagination || null;
        } else if (Array.isArray(responseData)) {
          wageSlipsData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        wageSlipsData = responseData.data;
      } else if (Array.isArray(responseData)) {
        wageSlipsData = responseData;
      }
      
      setWageSlips(wageSlipsData as WageSlip[]);
      setPagination(paginationData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to fetch wage slips");
      console.error("Failed to fetch wage slips:", err);
      setWageSlips([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const createWageSlip = useCallback(async (wageSlipData: any): Promise<WageSlip> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<ApiResponse<WageSlip>>('/wage-slips', wageSlipData);
      const newWageSlip = response.data.data;
      setWageSlips((prev) => [...prev, newWageSlip]);
      return newWageSlip;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const generateBulkWageSlips = useCallback(async (bulkData: any): Promise<WageSlip[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<ApiResponse<WageSlip[]>>('/wage-slips/generate', bulkData);
      const created = response.data.data || [];
      setWageSlips((prev) => [...prev, ...created]);
      return created;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateWageSlip = useCallback(async (id: number, wageSlipData: any): Promise<WageSlip> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put<ApiResponse<WageSlip>>(`/wage-slips/${id}`, wageSlipData);
      const updatedWageSlip = response.data.data;
      setWageSlips((prev) =>
        prev.map((slip) => (slip.id === id ? updatedWageSlip : slip))
      );
      return updatedWageSlip;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteWageSlip = useCallback(async (id: number): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/wage-slips/${id}`);
      setWageSlips((prev) => prev.filter((slip) => slip.id !== id));
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getWageSlip = useCallback(async (id: number): Promise<WageSlip> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<ApiResponse<WageSlip>>(`/wage-slips/${id}`);
      return response.data.data;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const contextValue: WageSlipContextType = {
    wageSlips,
    pagination: pagination || undefined,
    loading,
    error,
    fetchWageSlips,
    createWageSlip,
    generateBulkWageSlips,
    updateWageSlip,
    deleteWageSlip,
    getWageSlip,
  };

  return (
    <WageSlipContext.Provider value={contextValue}>
      {children}
    </WageSlipContext.Provider>
  );
};

export default WageSlipProvider;
